import LRUCache from './LRUCache.js';

/**
 * Get the environment global object (window in browser, globalThis/global in Node).
 */
function getGlobalObject() {
  if (typeof window !== 'undefined') return window;
  if (typeof globalThis !== 'undefined') return globalThis;
  if (typeof global !== 'undefined') return global;
  return {};
}

/**
 * Initialize window.__audioProbe test hook if not already set.
 */
function initAudioProbe() {
  const g = getGlobalObject();
  if (!g.__audioProbe) {
    g.__audioProbe = {
      events: [],
      enabled: true,
      getEvents() {
        return this.events;
      },
      clear() {
        this.events = [];
      },
      record(entry) {
        if (!this.enabled) return entry;
        this.events.push(entry);
        return entry;
      }
    };
  }
  return g.__audioProbe;
}

export class AudioEngine {
  /**
   * @param {Object} options Configuration options
   * @param {string} [options.mode='host'] 'host' (1.0 gain) or 'player' (0.3 audition gain)
   * @param {number} [options.gain=null] Explicit gain override
   * @param {number} [options.cacheCapacity=50] LRU buffer cache capacity
   * @param {AudioContext} [options.audioContext=null] Custom AudioContext instance (or mock)
   * @param {Function} [options.fetchFn=null] Custom fetch function for loading audio buffers
   */
  constructor(options = {}) {
    const {
      mode = 'host',
      gain = null,
      cacheCapacity = 50,
      audioContext = null,
      fetchFn = null
    } = options;

    this.mode = mode;
    this.targetGain = gain !== null ? gain : (mode === 'player' ? 0.3 : 1.0);
    this.lruCache = new LRUCache(cacheCapacity);
    this.fetchFn = fetchFn || (typeof fetch !== 'undefined' ? fetch.bind(getGlobalObject()) : null);

    this.listeners = new Map();
    this.probe = initAudioProbe();

    // AudioContext initialization
    this.audioContext = audioContext || this._createDefaultAudioContext();
    this.masterGainNode = null;

    if (this.audioContext) {
      this._initGainNode();
    }

    this.unlocked = Boolean(this.audioContext && this.audioContext.state === 'running');
    this.activeSequences = new Set();

    // Auto-attach unlock listeners on browser window if available
    this.setupUnlockListeners();
  }

  /**
   * Helper to lazy create AudioContext in browser environment
   */
  _createDefaultAudioContext() {
    const g = getGlobalObject();
    const AudioCtx = g.AudioContext || g.webkitAudioContext;
    if (AudioCtx) {
      try {
        return new AudioCtx();
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  /**
   * Initialize master GainNode connected to AudioContext destination
   */
  _initGainNode() {
    if (!this.audioContext || typeof this.audioContext.createGain !== 'function') return;
    try {
      this.masterGainNode = this.audioContext.createGain();
      const now = this.audioContext.currentTime || 0;
      if (this.masterGainNode.gain && typeof this.masterGainNode.gain.setValueAtTime === 'function') {
        this.masterGainNode.gain.setValueAtTime(this.targetGain, now);
      }
      if (this.audioContext.destination) {
        this.masterGainNode.connect(this.audioContext.destination);
      }
    } catch (e) {
      // Handle mock or non-standard environments
    }
  }

  /**
   * Get current context mode ('host' or 'player').
   * @returns {string}
   */
  getMode() {
    return this.mode;
  }

  /**
   * Set context mode ('host' = 1.0 gain, 'player' = 0.3 audition gain).
   * @param {string} mode
   */
  setMode(mode) {
    this.mode = mode;
    const newGain = mode === 'player' ? 0.3 : 1.0;
    this.setGain(newGain);
    this.emit('stateChange', { mode: this.mode, gain: newGain });
  }

  /**
   * Get current master gain value.
   * @returns {number}
   */
  getGain() {
    if (this.masterGainNode && this.masterGainNode.gain) {
      return typeof this.masterGainNode.gain.value === 'number'
        ? this.masterGainNode.gain.value
        : this.targetGain;
    }
    return this.targetGain;
  }

  /**
   * Set master gain value with optional linear ramp time.
   * @param {number} value Gain multiplier (e.g. 1.0 or 0.3)
   * @param {number} [rampTime=0] Ramp duration in seconds
   */
  setGain(value, rampTime = 0) {
    this.targetGain = value;
    if (this.masterGainNode && this.masterGainNode.gain) {
      const now = this.audioContext ? (this.audioContext.currentTime || 0) : 0;
      if (rampTime > 0 && typeof this.masterGainNode.gain.linearRampToValueAtTime === 'function') {
        this.masterGainNode.gain.setValueAtTime(this.masterGainNode.gain.value, now);
        this.masterGainNode.gain.linearRampToValueAtTime(value, now + rampTime);
      } else if (typeof this.masterGainNode.gain.setValueAtTime === 'function') {
        this.masterGainNode.gain.setValueAtTime(value, now);
      } else {
        this.masterGainNode.gain.value = value;
      }
    }
  }

  /**
   * Check if AudioContext is unlocked and running.
   * @returns {boolean}
   */
  isUnlocked() {
    return Boolean(this.unlocked && this.audioContext && this.audioContext.state === 'running');
  }

  /**
   * Explicit iOS/Safari unlock gate to resume AudioContext on user interaction.
   * @returns {Promise<boolean>}
   */
  async unlock() {
    if (!this.audioContext) {
      this.unlocked = true;
      return true;
    }

    if (this.audioContext.state === 'suspended' && typeof this.audioContext.resume === 'function') {
      try {
        await this.audioContext.resume();
      } catch (e) {
        // AudioContext resume error
      }
    }

    // Play silent buffer to unlock hardware on iOS
    try {
      if (typeof this.audioContext.createBuffer === 'function' && typeof this.audioContext.createBufferSource === 'function') {
        const buffer = this.audioContext.createBuffer(1, 1, 22050);
        const source = this.audioContext.createBufferSource();
        source.buffer = buffer;
        if (this.audioContext.destination) {
          source.connect(this.audioContext.destination);
        }
        source.start(0);
      }
    } catch (e) {
      // Silent buffer output ignored
    }

    this.unlocked = true;
    this.emit('unlocked', { state: this.audioContext.state || 'running' });
    return true;
  }

  /**
   * Attach user gesture listeners to document/window for auto-unlock on iOS/Safari.
   */
  setupUnlockListeners() {
    const g = getGlobalObject();
    if (typeof g.addEventListener !== 'function') return;

    const unlockHandler = async () => {
      await this.unlock();
      const events = ['touchstart', 'touchend', 'mousedown', 'keydown', 'click'];
      events.forEach(evt => {
        if (g.removeEventListener) {
          g.removeEventListener(evt, unlockHandler, true);
        }
      });
    };

    const events = ['touchstart', 'touchend', 'mousedown', 'keydown', 'click'];
    events.forEach(evt => {
      g.addEventListener(evt, unlockHandler, { once: true, capture: true });
    });
  }

  /**
   * Preload an audio clip into the LRU cache and decode it into an AudioBuffer.
   * @param {string} clipId Unique clip identifier
   * @param {AudioBuffer|ArrayBuffer|string} [source] Audio data, URL, or AudioBuffer object
   * @returns {Promise<AudioBuffer>}
   */
  async preload(clipId, source) {
    if (this.lruCache.has(clipId)) {
      return this.lruCache.get(clipId);
    }

    const data = source !== undefined ? source : clipId;

    // Case 1: AudioBuffer (or duck-typed buffer)
    if (data && typeof data === 'object' && typeof data.duration === 'number') {
      this.lruCache.set(clipId, data);
      return data;
    }

    // Case 2: ArrayBuffer
    if (data instanceof ArrayBuffer || (data && data.byteLength !== undefined)) {
      const decoded = await this._decodeArrayBuffer(data);
      this.lruCache.set(clipId, decoded);
      return decoded;
    }

    // Case 3: URL String or fetch
    if (typeof data === 'string') {
      if (!this.fetchFn) {
        throw new Error(`Cannot fetch audio URL "${data}" without a fetch function or browser environment.`);
      }
      const response = await this.fetchFn(data);
      const arrayBuffer = typeof response.arrayBuffer === 'function'
        ? await response.arrayBuffer()
        : response;
      const decoded = await this._decodeArrayBuffer(arrayBuffer);
      this.lruCache.set(clipId, decoded);
      return decoded;
    }

    throw new Error(`Invalid audio source provided for clipId "${clipId}"`);
  }

  /**
   * Decode ArrayBuffer into AudioBuffer using AudioContext
   */
  async _decodeArrayBuffer(arrayBuffer) {
    if (this.audioContext && typeof this.audioContext.decodeAudioData === 'function') {
      return new Promise((resolve, reject) => {
        const res = this.audioContext.decodeAudioData(
          arrayBuffer,
          buf => resolve(buf),
          err => reject(err)
        );
        if (res && typeof res.then === 'function') {
          res.then(resolve).catch(reject);
        }
      });
    }
    // Mock decode fallback if AudioContext is not available or incomplete
    return {
      duration: 3.0,
      numberOfChannels: 2,
      sampleRate: 44100,
      length: 44100 * 3
    };
  }

  /**
   * Preload multiple audio clips.
   * @param {Array<string|{clipId: string, source: *}>} items
   * @returns {Promise<Array<AudioBuffer>>}
   */
  async preloadMany(items) {
    return Promise.all(items.map(item => {
      if (typeof item === 'string') {
        return this.preload(item);
      }
      return this.preload(item.clipId, item.source || item.url);
    }));
  }

  /**
   * Get loaded AudioBuffer from LRU cache.
   * @param {string} clipId
   * @returns {AudioBuffer|undefined}
   */
  getBuffer(clipId) {
    return this.lruCache.get(clipId);
  }

  /**
   * Check if AudioBuffer is cached.
   * @param {string} clipId
   * @returns {boolean}
   */
  hasBuffer(clipId) {
    return this.lruCache.has(clipId);
  }

  /**
   * Sample-accurate gapless multi-clip sequencing with configurable per-clip gap durations.
   * Records details to window.__audioProbe test hook.
   *
   * @param {Array<string|Object>} clips List of clip IDs or objects:
   *   { clipId: string, buffer?: AudioBuffer, gap?: number, delay?: number, startOffset?: number, duration?: number, gain?: number }
   * @param {Object} [options={}] Playback options
   * @param {number} [options.startAt] Scheduled Web Audio startTime (defaults to currentTime + 0.05)
   * @param {number} [options.defaultGap=0] Default gap in seconds between clips if not specified
   * @param {Function} [options.onProgress] Progress callback function
   * @param {Function} [options.onEnded] Completion callback function
   * @returns {Object} Playback control handle: { stop, pause, resume, getProgress, isPlaying }
   */
  playSequence(clips, options = {}) {
    if (!Array.isArray(clips) || clips.length === 0) {
      throw new Error('playSequence requires a non-empty array of clips.');
    }

    const sequenceId = `seq_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    const ctxTime = this.audioContext ? (this.audioContext.currentTime || 0) : 0;
    const startTime = options.startAt !== undefined ? options.startAt : (ctxTime + 0.05);
    const defaultGap = options.defaultGap || 0;

    let scheduledTime = startTime;
    const scheduledItems = [];

    for (let i = 0; i < clips.length; i++) {
      const rawItem = clips[i];
      const item = typeof rawItem === 'string' ? { clipId: rawItem } : rawItem;
      const clipId = item.clipId || `clip_${i}`;

      const buffer = item.buffer || this.lruCache.get(clipId) || item.audioBuffer;
      if (!buffer) {
        throw new Error(`Audio buffer for clip "${clipId}" is not loaded in cache. Call preload() first.`);
      }

      // Configurable gap before clip
      const gap = item.gap !== undefined ? item.gap : (item.delay !== undefined ? item.delay : (i === 0 ? 0 : defaultGap));
      scheduledTime += gap;

      const clipScheduledAt = scheduledTime;
      const startOffset = item.startOffset || 0;
      const duration = item.duration !== undefined ? item.duration : Math.max(0, (buffer.duration || 0) - startOffset);
      const clipGainMultiplier = item.gain !== undefined ? item.gain : 1.0;
      const effectiveGain = clipGainMultiplier * this.getGain();

      let sourceNode = null;
      let gainNode = null;

      if (this.audioContext && typeof this.audioContext.createBufferSource === 'function') {
        try {
          sourceNode = this.audioContext.createBufferSource();
          sourceNode.buffer = buffer;

          if (typeof this.audioContext.createGain === 'function') {
            gainNode = this.audioContext.createGain();
            if (gainNode.gain && typeof gainNode.gain.setValueAtTime === 'function') {
              gainNode.gain.setValueAtTime(clipGainMultiplier, clipScheduledAt);
            }
            sourceNode.connect(gainNode);
            if (this.masterGainNode) {
              gainNode.connect(this.masterGainNode);
            } else if (this.audioContext.destination) {
              gainNode.connect(this.audioContext.destination);
            }
          } else if (this.masterGainNode) {
            sourceNode.connect(this.masterGainNode);
          }

          if (typeof sourceNode.start === 'function') {
            sourceNode.start(clipScheduledAt, startOffset, duration);
          }
        } catch (e) {
          // Web Audio node creation error in test environment
        }
      }

      // Helper to round timestamps to microsecond resolution
      const roundTime = (t) => (t === null || t === undefined ? t : Math.round(t * 1e6) / 1e6);

      // Record entry in window.__audioProbe test hook
      const probeRecord = this.probe.record({
        clipId,
        scheduledAt: roundTime(clipScheduledAt),
        startOffset: roundTime(startOffset),
        gain: Math.round(effectiveGain * 1e6) / 1e6,
        completedAt: null
      });

      const itemInfo = {
        clipId,
        index: i,
        scheduledAt: clipScheduledAt,
        duration,
        startOffset,
        effectiveGain,
        sourceNode,
        gainNode,
        probeRecord
      };

      if (sourceNode) {
        const onEndedHandler = () => {
          const compTime = clipScheduledAt + duration;
          probeRecord.completedAt = roundTime(compTime);
          this.emit('clipEnd', { sequenceId, clipId, index: i, completedAt: roundTime(compTime) });
        };
        sourceNode.onended = onEndedHandler;
      }

      scheduledItems.push(itemInfo);
      scheduledTime += duration;
    }

    const sequenceDuration = scheduledTime - startTime;
    let playing = true;
    let paused = false;
    let pauseTime = 0;

    // Progress emitter timer
    const progressInterval = setInterval(() => {
      if (!playing || paused) return;
      const currentCtxTime = this.audioContext ? (this.audioContext.currentTime || 0) : 0;
      const elapsed = Math.max(0, currentCtxTime - startTime);
      const progress = sequenceDuration > 0 ? Math.min(1.0, elapsed / sequenceDuration) : 1.0;

      // Find active clip
      let activeItem = scheduledItems[0];
      for (const item of scheduledItems) {
        if (currentCtxTime >= item.scheduledAt) {
          activeItem = item;
        }
      }

      const progressData = {
        sequenceId,
        clipId: activeItem ? activeItem.clipId : null,
        clipIndex: activeItem ? activeItem.index : 0,
        totalClips: scheduledItems.length,
        currentTime: elapsed,
        duration: sequenceDuration,
        progress
      };

      this.emit('progress', progressData);
      if (options.onProgress) {
        options.onProgress(progressData);
      }

      if (elapsed >= sequenceDuration) {
        playing = false;
        clearInterval(progressInterval);

        // Mark any remaining incomplete probe records as completed
        scheduledItems.forEach(item => {
          if (item.probeRecord.completedAt === null) {
            item.probeRecord.completedAt = item.scheduledAt + item.duration;
          }
        });

        this.activeSequences.delete(sequenceId);
        this.emit('ended', { sequenceId });
        if (options.onEnded) {
          options.onEnded({ sequenceId });
        }
      }
    }, 20);
    if (progressInterval && typeof progressInterval.unref === 'function') {
      progressInterval.unref();
    }

    const controlHandle = {
      sequenceId,
      scheduledItems,
      stop: () => {
        playing = false;
        clearInterval(progressInterval);
        scheduledItems.forEach(item => {
          if (item.sourceNode && typeof item.sourceNode.stop === 'function') {
            try {
              item.sourceNode.stop();
            } catch (e) {}
          }
        });
        this.activeSequences.delete(sequenceId);
      },
      pause: () => {
        if (!playing || paused) return;
        paused = true;
        pauseTime = this.audioContext ? (this.audioContext.currentTime || 0) : 0;
      },
      resume: () => {
        if (!playing || !paused) return;
        paused = false;
      },
      getProgress: () => {
        const currentCtxTime = this.audioContext ? (this.audioContext.currentTime || 0) : 0;
        const elapsed = Math.max(0, currentCtxTime - startTime);
        return sequenceDuration > 0 ? Math.min(1.0, elapsed / sequenceDuration) : 1.0;
      },
      isPlaying: () => playing && !paused
    };

    this.activeSequences.add(sequenceId);
    return controlHandle;
  }

  /**
   * Convenience method to play a single clip as a sequence.
   * @param {string|Object} clipIdOrObj Clip ID string or clip config object
   * @param {Object} [options] Sequence options
   */
  playClip(clipIdOrObj, options = {}) {
    const item = typeof clipIdOrObj === 'string' ? { clipId: clipIdOrObj } : clipIdOrObj;
    return this.playSequence([item], options);
  }

  /**
   * Alias for playClip.
   */
  play(clipIdOrObj, options = {}) {
    return this.playClip(clipIdOrObj, options);
  }

  /**
   * Convenience method to audition a clip in player mode (0.3 gain).
   * @param {string|Object} clipIdOrObj
   * @param {Object} [options]
   */
  audition(clipIdOrObj, options = {}) {
    const prevMode = this.mode;
    const prevGain = this.getGain();

    this.setMode('player');
    const handle = this.playClip(clipIdOrObj, {
      ...options,
      onEnded: (evt) => {
        this.setMode(prevMode);
        this.setGain(prevGain);
        if (options.onEnded) options.onEnded(evt);
      }
    });

    return handle;
  }

  // --- Event Emitter implementation ---

  on(event, listener) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(listener);
    return this;
  }

  off(event, listener) {
    if (!this.listeners.has(event)) return this;
    const arr = this.listeners.get(event).filter(l => l !== listener);
    this.listeners.set(event, arr);
    return this;
  }

  once(event, listener) {
    const wrapper = (...args) => {
      this.off(event, wrapper);
      listener(...args);
    };
    return this.on(event, wrapper);
  }

  emit(event, payload) {
    if (!this.listeners.has(event)) return false;
    this.listeners.get(event).forEach(listener => {
      try {
        listener(payload);
      } catch (e) {
        console.error(`Error in AudioEngine event listener for "${event}":`, e);
      }
    });
    return true;
  }
}

export default AudioEngine;
